import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  getAuth,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

import {
  getStaffMagicLinkCooldown,
  normalizeStaffEmail,
  type StaffRole,
} from "./auth-contract";
import { firebaseApp } from "./firebase";

const staffMagicEmailKey = "monarch:staff-magic-email";
const staffMagicRequestedAtKey = "monarch:staff-magic-requested-at";
let magicLinkRequest: Promise<void> | null = null;

export type StaffInvitationInput = {
  displayName: string;
  email: string;
  role: StaffRole;
};

type StaffInvitationResult = { email: string; uid: string };
export type StaffAuthEmailPurpose =
  "password_reset" | "sign_in" | "verify_email";

export type CentralStaffMember = {
  active: boolean;
  displayName: string;
  email: string;
  id: string;
  invitedAt: string | null;
  locationId: string | null;
  locationName: string | null;
  locationRestricted: boolean;
  role: StaffRole;
  status: string;
  updatedAt: string | null;
  verification: {
    reasons: Array<"active" | "location" | "role" | "tenant">;
    status: "mismatch" | "missing-auth" | "verified";
  };
};
export type CentralStaffLocation = {
  active: boolean;
  id: string;
  name: string;
};
export type CentralStaffAuditEvent = {
  actorName: string;
  actorUid: string;
  createdAt: string | null;
  detail: string;
  subjectName: string;
  subjectUid: string;
  type: string;
};

export async function inviteStaffMember(input: StaffInvitationInput) {
  const callable = httpsCallable<StaffInvitationInput, StaffInvitationResult>(
    getFunctions(firebaseApp, "us-east1"),
    "inviteStaffMember",
  );
  const result = await callable(input);
  return result.data;
}

export async function listCentralStaffMembers() {
  const callable = httpsCallable<
    undefined,
    {
      auditEvents: CentralStaffAuditEvent[];
      locations: CentralStaffLocation[];
      permissionMatrix: Record<string, readonly string[]>;
      staff: CentralStaffMember[];
    }
  >(getFunctions(firebaseApp, "us-east1"), "listCentralStaffMembers");
  return (await callable(undefined)).data;
}

export async function updateCentralStaffMember(input: {
  uid: string;
  updates: {
    active?: boolean;
    locationId?: string | null;
    resetAccess?: true;
    role?: StaffRole;
  };
}) {
  const callable = httpsCallable<
    typeof input,
    {
      active: boolean;
      id: string;
      locationId: string | null;
      role: StaffRole;
      sessionRevoked: boolean;
      status: string;
    }
  >(getFunctions(firebaseApp, "us-east1"), "updateCentralStaffMember");
  return (await callable(input)).data;
}

export async function requestStaffMagicLogin(
  email: string,
  options: { now?: number; persistEmail?: boolean } = {},
) {
  if (magicLinkRequest) return magicLinkRequest;
  magicLinkRequest = (async () => {
    const now = options.now ?? Date.now();
    const normalizedEmail = normalizeStaffEmail(email);
    const lastRequestedAt = Number(
      await AsyncStorage.getItem(staffMagicRequestedAtKey),
    );
    if (
      getStaffMagicLinkCooldown(
        Number.isFinite(lastRequestedAt) ? lastRequestedAt : null,
        now,
      ) > 0
    ) {
      throw codedError(
        "auth/too-many-requests",
        "Wait before requesting another secure sign-in link.",
      );
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      throw codedError(
        "auth/invalid-email",
        "Enter a valid staff email address.",
      );
    }
    await requestStaffAuthEmail(normalizedEmail, "sign_in");
    await AsyncStorage.setItem(staffMagicRequestedAtKey, String(now));
    if (options.persistEmail !== false) {
      await AsyncStorage.setItem(staffMagicEmailKey, normalizedEmail);
    }
  })().finally(() => {
    magicLinkRequest = null;
  });
  return magicLinkRequest;
}

export async function requestStaffAuthEmail(
  email: string,
  purpose: StaffAuthEmailPurpose,
) {
  const normalizedEmail = normalizeStaffEmail(email);
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
    throw codedError(
      "auth/invalid-email",
      "Enter a valid staff email address.",
    );
  }
  const callable = httpsCallable<
    { email: string; purpose: StaffAuthEmailPurpose; returnOrigin?: string },
    { accepted: true }
  >(getFunctions(firebaseApp, "us-east1"), "requestCentralStaffMagicLogin");
  await callable({ email: normalizedEmail, purpose, ...(typeof window !== "undefined" && ["https://central.wehonor.app", "https://monarchcentral.web.app", "https://monarchcentral--register-central-test-t0rk9cmb.web.app"].includes(window.location.origin) ? { returnOrigin: window.location.origin } : {}) });
}

export function isStaffMagicLoginLink(url: string) {
  return isSignInWithEmailLink(getAuth(firebaseApp), url);
}

export async function completeStaffMagicLogin(url: string, email?: string) {
  if (!isStaffMagicLoginLink(url)) {
    throw codedError("auth/invalid-action-code", "Invalid staff sign-in link.");
  }
  const normalizedEmail = normalizeStaffEmail(
    email || (await AsyncStorage.getItem(staffMagicEmailKey)) || "",
  );
  if (!normalizedEmail) {
    throw codedError(
      "auth/invalid-email",
      "Enter the staff email address that requested this link.",
    );
  }
  await signInWithEmailLink(getAuth(firebaseApp), normalizedEmail, url);
  await AsyncStorage.multiRemove([
    staffMagicEmailKey,
    staffMagicRequestedAtKey,
  ]);
}

export async function recordCentralStaffLoginAudit(
  method: "magic_link" | "password",
) {
  const callable = httpsCallable<
    { method: "magic_link" | "password" },
    { recorded: boolean }
  >(getFunctions(firebaseApp, "us-east1"), "recordCentralStaffLoginAudit");
  return (await callable({ method })).data;
}

export function getStaffInvitationMessage(error: unknown) {
  const code = getErrorCode(error);
  if (
    code === "functions/permission-denied" ||
    code === "functions/unauthenticated"
  ) {
    return "Your administrator verification has expired. Sign in again and retry the invitation.";
  }
  if (code === "functions/already-exists") {
    return "A staff account already uses that email address.";
  }
  if (code === "functions/invalid-argument") {
    return "Check the staff name, email, and role, then try again.";
  }
  if (code === "functions/failed-precondition") {
    return "This administrator change is not allowed for the current account.";
  }
  if (code === "functions/not-found") {
    return "That staff account is no longer available in this funeral home. Refresh the directory.";
  }
  if (code === "auth/too-many-requests") {
    return "Too many secure email requests were made. Wait a few minutes, then try again.";
  }
  if (code === "auth/network-request-failed") {
    return "Central couldn’t reach the sign-in service. Check your connection and try again.";
  }
  return "Central couldn’t complete this staff access action. Try again.";
}

function getErrorCode(error: unknown) {
  if (
    typeof error === "object" &&
    error &&
    "code" in error &&
    typeof error.code === "string"
  ) {
    return error.code;
  }
  return "unknown";
}

function codedError(code: string, message: string) {
  return Object.assign(new Error(message), { code });
}
